import { Box, Grid, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { setNewMortgageAction } from "../../../../contexts/mortgage/Actions";
import { useMortgageDispatch, useMortgageState } from "../../../../contexts/mortgage/Reducer";
import { useFinanceCalculatorService } from "../../../../services/finance-calculator/FinanceCalculatorServiceContext";
import { addYears, DateFormat, formatDate } from "../../../../utils/DateUtils";
import { parseNumber } from "../../../../utils/NumberUtils";
import ErrorMessage from "../../../feedback/ErrorMessage";
import ConfiguratorInnerBox from "./ConfiguratorInnerBox";
import MortgageSlider from "./MortageSlider";

const DurationBox = styled(Box)(({ theme }) => ({
    marginTop: "17px",
    marginBottom: "8px",
    [theme.breakpoints.up("md")]: {
        marginTop: "17px",
        marginBottom: "8px",
    },
    [theme.breakpoints.up("lg")]: {
        marginTop: "34px",
        marginBottom: "49px",
    },
}));

const DurationConfigurator = () => {
    const { t } = useTranslation();
    const { mortgage, newMortgage } = useMortgageState();
    const [minDuration, setMinDuration] = useState(0);
    const [maxDuration, setMaxDuration] = useState(100);
    const mortgageDispatch = useMortgageDispatch();
    const financeCalculatorService = useFinanceCalculatorService();

    useEffect(() => {
        getDurationRange();
    }, []);

    if (mortgage === undefined || newMortgage === undefined) {
        return <ErrorMessage />;
    }

    const getDurationRange = () => {
        let minValue = 100;
        let maxValue = 0;

        for (const offer of newMortgage.offers) {
            if (offer.duration > maxValue) {
                maxValue = offer.duration;
            }
            if (offer.duration < minValue) {
                minValue = offer.duration;
            }
        }
        setMinDuration(minValue);
        setMaxDuration(maxValue);
    };

    const onSliderChange = (newValue: number) => {
        const newMortgageCopy = { ...newMortgage };
        if (newValue !== parseNumber(newMortgage.duration)) {
            newMortgageCopy.duration = newValue.toString();
            financeCalculatorService.onDurationChange(newMortgageCopy);
            mortgageDispatch(setNewMortgageAction(newMortgageCopy));
        }
    };

    const isDurationUpdated = () => {
        return Number(newMortgage.duration) !== 0;
    };

    return (
        <Grid item sm={12} lg={3.7}>
            <ConfiguratorInnerBox>
                <Typography variant="subtitle1" component="div" data-cy={"adjust-mortgage"}>
                    {t("Actions.Adjust Mortgage")}
                </Typography>
                <DurationBox>
                    {isDurationUpdated() ? (
                        <Typography variant="body1" component="div" data-cy={"number-of-years"}>
                            {newMortgage.duration} {t("General.Years")}
                        </Typography>
                    ) : (
                        <Typography variant="body1" component="div" data-cy={"set-years"}>
                            {t("ConfigurationScreen.Set years")}
                        </Typography>
                    )}
                    {isDurationUpdated() ? (
                        <Typography
                            variant="caption"
                            component="div"
                            color={"rgba(0,0,0,0.54)"}
                            data-cy={"end-date-info"}>
                            {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                            {addYears(newMortgage.expirationDate, +newMortgage.duration)}
                        </Typography>
                    ) : (
                        <Typography
                            variant="caption"
                            component="div"
                            color={"rgba(0,0,0,0.54)"}
                            data-cy={"format-of-end-date"}>
                            {formatDate(newMortgage.expirationDate, DateFormat.DATE)} -{" "}
                            {t(`General.${DateFormat.DATE}`)}
                        </Typography>
                    )}
                </DurationBox>
                <MortgageSlider
                    min={minDuration}
                    max={maxDuration}
                    onSliderChange={onSliderChange}
                    sliderStep={1}
                    value={Number(newMortgage.duration)}
                    selector={"duration-slider"}
                />
            </ConfiguratorInnerBox>
        </Grid>
    );
};

export default DurationConfigurator;
